/**
 * Task #3406 — Incentivazione (commissioning): schemi zod dei boundary input.
 *
 * SoT analisi: docs/incentivazione-analisi.md. I tipi dei body validati
 * DEVONO essere derivati da `z.infer<typeof ...>` (docs/zod-boundary.md).
 * Le route CRUD (task API) consumano questi schemi via `validate(...)`.
 */
import { z } from 'zod';
import { commissioningCompetitionContextInputSchema, commissioningParticipantTypeAllowed } from './commissioning-competition-context';
import { commissioningTemplateSimulationEventSchema, commissioningThresholdPeriodScenarioSchema } from './commissioning-threshold-simulation';
import {
  COMMISSIONING_ACK_ACTIONS,
  COMMISSIONING_ADDON_ANSWER_STATES,
  COMMISSIONING_ADDON_PARAM_TYPES,
  COMMISSIONING_CAP_METRICS,
  COMMISSIONING_CAP_TYPES,
  COMMISSIONING_CLUSTER_ENTITY_TYPES,
  COMMISSIONING_COMPETITION_FAMILIES,
  COMMISSIONING_COMPETITION_STATUSES,
  COMMISSIONING_CONDITION_OPERATORS,
  COMMISSIONING_EDGE_CONDITION_METRICS,
  COMMISSIONING_EDGE_CONDITION_OPERATORS,
  COMMISSIONING_INSTANCE_SCOPES,
  COMMISSIONING_INTERACTION_EDGE_TYPES,
  COMMISSIONING_MOLTIPLICATORE_TARGETS,
  COMMISSIONING_THRESHOLD_COUNT_KINDS,
  COMMISSIONING_THRESHOLD_SOURCES,
  COMMISSIONING_VARIABLE_DATA_TYPES,
  COMMISSIONING_VP_EVENT_FAMILIES,
  commissioningCrossRefCombinesForMeasure,
  type CommissioningGateV2,
  type CommissioningThresholdBandStructure,
  type CommissioningThresholdDeclaration,
  type CommissioningThresholdSource,
} from '../types/commissioning';
// Task #3453 — kernel v2: AST superset + metadata variabili.
import {
  commissioningKernelNodeSchema,
  commissioningVariableKernelFieldsSchema,
} from './commissioning-kernel';
import { commissioningAggregateRulesSchema } from './commissioning-runtime-v3';
import {
  commissioningThresholdCategorySchema,
  commissioningThresholdCategoryStructureSchema,
  commissioningThresholdSurchargeSchema,
  commissioningThresholdSurchargeStructureSchema,
} from './commissioning-threshold-economics';
import { commissioningThresholdCountModeSchema, commissioningThresholdPaymentUnitSchema } from './commissioning-threshold-measures';
import { COMMISSIONING_THRESHOLD_SOURCE_FIELD, commissioningThresholdValuesSchema } from './commissioning-threshold-values';

/** Server-owned policy captured in the existing rule-version variants.
 * `version` is a stored invariant (always 2: every competition carries the
 * source-admission contract, enforced by the database CHECK), not a
 * discriminator: `declaredSource` null = every source of the family. */
export const commissioningEventAdmissionSnapshotSchema = z.object({
  version: z.literal(2),
  declaredSource: z.enum(['sell_out', 'sell_in', 'crm']).nullable(),
}).strict();
export type CommissioningEventAdmissionSnapshot = z.infer<typeof commissioningEventAdmissionSnapshotSchema>;

// ---------------------------------------------------------------------------
// Primitive riusabili
// ---------------------------------------------------------------------------

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data in formato YYYY-MM-DD');

/** Codice macchina: snake_case minuscolo. */
const machineCodeSchema = z
  .string()
  .min(1)
  .max(60)
  .regex(/^[a-z][a-z0-9_]*$/, 'Codice: minuscolo, snake_case, inizia con lettera');

/** Dot-notation resolver (es. sale.valenza, period_aggregate.tasso_mnp_out). */
const resolverKeySchema = z
  .string()
  .min(3)
  .max(160)
  .regex(
    /^[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)+$/,
    'resolver_key in dot-notation (es. sale.valenza)'
  );

const scalarValueSchema = z.union([z.string(), z.number(), z.boolean()]);

// ---------------------------------------------------------------------------
// L1 — Categorie e variabili
// ---------------------------------------------------------------------------

export const createCommissioningVariableCategorySchema = z.object({
  code: machineCodeSchema,
  name: z.string().min(1).max(120),
  description: z.string().max(2000).nullable().optional(),
  sort_order: z.number().int().min(0).optional(),
  is_active: z.boolean().optional(),
});
export const updateCommissioningVariableCategorySchema =
  createCommissioningVariableCategorySchema.partial();

const commissioningRatioSideSchema = z.object({
  measure: z.enum(['line_count', 'valenza_sum', 'amount_sum']),
  filterFunctionId: z.string().uuid().optional(),
});

export const commissioningCrossRefMeasureSchema = z.enum([
  'valenza_sum',
  'line_count',
  'amount_sum',
  'ratio',
  'tgt_pct',
  'threshold_reached',
  'effective_band_id',
  'prize_earned',
  'malus_count',
  'bonus_count',
  'bonus_malus_sum',
]);

/** Finestra sui periodi della gara sorgente (specchio di CommissioningCrossRefWindow). */
const commissioningCrossRefWindowSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('overlap') }),
  z.object({ kind: z.literal('previous_periods'), count: z.number().int().min(1).max(36) }),
  z.object({ kind: z.literal('source_to_date') }),
  z.object({ kind: z.literal('source_full') }),
]);

/** Combinazione multi-periodo (specchio di CommissioningCrossRefCombine). */
const commissioningCrossRefCombineSchema = z.enum(['sum', 'avg', 'min', 'all', 'any', 'last']);
/** Misure booleane: ammettono solo all/any/last. */
const CROSS_REF_BOOLEAN_MEASURES: ReadonlyArray<string> = ['threshold_reached'];

/** Misure cross_ref lette live dal ledger referenziato (filtrabili per-riga). */
const CROSS_REF_LEDGER_MEASURES: ReadonlyArray<string> = [
  'valenza_sum',
  'line_count',
  'amount_sum',
];
/** Misure cross_ref ammesse con bersaglio driver di gara (specchio di COMMISSIONING_CROSS_REF_DRIVER_MEASURES). */
const CROSS_REF_DRIVER_MEASURES: ReadonlyArray<string> = [
  'valenza_sum',
  'line_count',
  'amount_sum',
  'ratio',
  'tgt_pct',
  'malus_count',
  'bonus_count',
  'bonus_malus_sum',
];
export const commissioningVariableComputeConfigSchema = z
  .object({
    window: z.enum(['period', 'rolling_30d', 'rolling_90d']),
    aggregation: z.enum(['sum', 'count', 'avg', 'ratio', 'line_total_sum', 'cross_ref']),
    /** Filtro L3 opzionale per aggregazioni sum/count/avg (non usato per ratio/cross_ref). */
    filterFunctionId: z.string().uuid().optional(),
    /** Lato numeratore (Contatore A) — obbligatorio per ratio e per cross_ref+refMeasure='ratio'. */
    numerator: commissioningRatioSideSchema.optional(),
    /** Lato denominatore (Contatore B) — obbligatorio per ratio e per cross_ref+refMeasure='ratio'. */
    denominator: commissioningRatioSideSchema.optional(),
    /** UUID istanza configuratore referenziata — cross_ref: esattamente uno fra refInstanceId e refDriver. */
    refInstanceId: z.string().uuid().optional(),
    /** Driver di gara referenziato (tutte le istanze attive del driver nella gara) — alternativo a refInstanceId. */
    refDriver: z.object({
      competitionId: z.string().uuid(),
      businessDriverId: z.string().uuid(),
    }).optional(),
    /** Misura da leggere dall'istanza referenziata — obbligatorio se aggregation='cross_ref'. */
    refMeasure: commissioningCrossRefMeasureSchema.optional(),
    /** Filtro L3 per-riga sul ledger referenziato — solo cross_ref con misura ledger. */
    refFilterFunctionId: z.string().uuid().optional(),
    /** Finestra sui periodi della sorgente — solo cross_ref; assente = overlap (storico). */
    refWindow: commissioningCrossRefWindowSchema.optional(),
    /** Combinazione multi-periodo — solo cross_ref; assente = default per misura. */
    refCombine: commissioningCrossRefCombineSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.aggregation === 'ratio') {
      if (!v.numerator) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['numerator'],
          message: "Il numeratore è obbligatorio per aggregation='ratio'",
        });
      }
      if (!v.denominator) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['denominator'],
          message: "Il denominatore è obbligatorio per aggregation='ratio'",
        });
      }
    } else if (v.aggregation === 'cross_ref') {
      const bandIdentity = v.refMeasure === 'effective_band_id';
      if (bandIdentity) {
        if (v.refInstanceId || v.refDriver) ctx.addIssue({
          code: z.ZodIssueCode.custom, path: ['refInstanceId'],
          message: 'La banda effettiva usa il collegamento della ricetta nella gara, non un bersaglio nel catalogo',
        });
        if (v.window !== 'period') ctx.addIssue({
          code: z.ZodIssueCode.custom, path: ['window'], message: 'Una banda appartiene a un periodo della sorgente',
        });
        if (v.filterFunctionId !== undefined) ctx.addIssue({
          code: z.ZodIssueCode.custom, path: ['filterFunctionId'], message: 'Una banda già valutata non può essere rifiltrata per evento',
        });
      }
      if (!bandIdentity && !v.refInstanceId && !v.refDriver) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['refInstanceId'],
          message: "cross_ref richiede un bersaglio: refInstanceId (configuratore) oppure refDriver (driver di gara)",
        });
      }
      if (v.refInstanceId && v.refDriver) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['refDriver'],
          message: 'refInstanceId e refDriver sono mutuamente esclusivi',
        });
      }
      if (v.refDriver && v.refMeasure !== undefined
        && !CROSS_REF_DRIVER_MEASURES.includes(v.refMeasure)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['refMeasure'],
          message: 'Con refDriver sono ammesse solo le misure line_count/valenza_sum/amount_sum/ratio/tgt_pct/malus_count/bonus_count/bonus_malus_sum',
        });
      }
      if (!v.refMeasure) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['refMeasure'],
          message: "refMeasure è obbligatorio per aggregation='cross_ref'",
        });
      }
      if (v.refCombine !== undefined && v.refMeasure !== undefined) {
        const allowed = commissioningCrossRefCombinesForMeasure(v.refMeasure);
        if (!allowed.includes(v.refCombine)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['refCombine'],
            message: bandIdentity ? 'Le identità delle bande non si sommano: seleziona l’ultimo periodo della finestra'
              : CROSS_REF_BOOLEAN_MEASURES.includes(v.refMeasure)
              ? "Con una misura booleana (threshold_reached) sono ammesse solo le combinazioni all/any/last"
              : "Le combinazioni all/any sono ammesse solo con misure booleane (threshold_reached)",
          });
        }
      }
      if (v.refMeasure === 'ratio') {
        // Ratio cross-ref: entrambi i lati obbligatori, filtro top-level vietato
        // (i filtri per-riga stanno DENTRO i lati numerator/denominator).
        if (!v.numerator) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['numerator'],
            message: "Il numeratore è obbligatorio per cross_ref con refMeasure='ratio'",
          });
        }
        if (!v.denominator) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['denominator'],
            message: "Il denominatore è obbligatorio per cross_ref con refMeasure='ratio'",
          });
        }
        if (v.refFilterFunctionId !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['refFilterFunctionId'],
            message: "refFilterFunctionId non è ammesso con refMeasure='ratio': usa i filtri dei lati",
          });
        }
      } else {
        if (v.numerator !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['numerator'],
            message: "numerator è valido solo per cross_ref con refMeasure='ratio'",
          });
        }
        if (v.denominator !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['denominator'],
            message: "denominator è valido solo per cross_ref con refMeasure='ratio'",
          });
        }
        if (v.refFilterFunctionId !== undefined
          && (v.refMeasure === undefined || !CROSS_REF_LEDGER_MEASURES.includes(v.refMeasure))) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['refFilterFunctionId'],
            message: 'refFilterFunctionId è ammesso solo con misure ledger (valenza_sum/line_count/amount_sum)',
          });
        }
      }
    } else {
      if (v.numerator !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['numerator'],
          message: "numerator è valido solo per aggregation='ratio'",
        });
      }
      if (v.denominator !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['denominator'],
          message: "denominator è valido solo per aggregation='ratio'",
        });
      }
    }
    if (v.aggregation !== 'cross_ref') {
      if (v.refInstanceId !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['refInstanceId'],
          message: "refInstanceId è valido solo per aggregation='cross_ref'",
        });
      }
      if (v.refDriver !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['refDriver'],
          message: "refDriver è valido solo per aggregation='cross_ref'",
        });
      }
      if (v.refMeasure !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['refMeasure'],
          message: "refMeasure è valido solo per aggregation='cross_ref'",
        });
      }
      if (v.refFilterFunctionId !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['refFilterFunctionId'],
          message: "refFilterFunctionId è valido solo per aggregation='cross_ref'",
        });
      }
      if (v.refWindow !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['refWindow'],
          message: "refWindow è valido solo per aggregation='cross_ref'",
        });
      }
      if (v.refCombine !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['refCombine'],
          message: "refCombine è valido solo per aggregation='cross_ref'",
        });
      }
    }
  });

export const createCommissioningVariableSchema = z
  .object({
    category_id: z.string().uuid().nullable().optional(),
    code: z.string().min(1).max(80).regex(/^[a-z][a-z0-9_]*$/),
    name: z.string().min(1).max(160),
    description: z.string().max(2000).nullable().optional(),
    resolver_key: resolverKeySchema,
    data_type: z.enum(COMMISSIONING_VARIABLE_DATA_TYPES).optional(),
    is_computed: z.boolean().optional(),
    compute_config: commissioningVariableComputeConfigSchema.nullable().optional(),
    valid_from: isoDateSchema.nullable().optional(),
    valid_to: isoDateSchema.nullable().optional(),
    is_active: z.boolean().optional(),
  })
  // Task #3453 — metadata kernel (sorgente/soggetto/fase/cardinalità/…);
  // data_type viene ampliato ai tipi kernel (datetime/duration/select).
  .merge(commissioningVariableKernelFieldsSchema)
  .superRefine((v, ctx) => {
    if (v.valid_from && v.valid_to && v.valid_from > v.valid_to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['valid_to'],
        message: 'valid_to deve essere >= valid_from',
      });
    }
    const bandIdentity = v.compute_config?.aggregation === 'cross_ref' && v.compute_config.refMeasure === 'effective_band_id';
    if (bandIdentity) {
      const expected = {
        is_computed: true, resolver_key: `threshold_state.${v.code}`, data_type: 'text',
        source_kind: 'aggregate', subject_kind: 'period', min_phase: 'validation', cardinality: 'scalar',
      } as const;
      for (const key of Object.keys(expected) as Array<keyof typeof expected>) {
        if (v[key] !== expected[key]) ctx.addIssue({
          code: z.ZodIssueCode.custom, path: [key], message: `La banda effettiva richiede ${key}=${String(expected[key])}`,
        });
      }
    }
    if (v.is_computed && !bandIdentity && !v.resolver_key.startsWith('period_aggregate.')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['resolver_key'],
        message: 'Le variabili computed usano il ramo period_aggregate.*',
      });
    }
    if (v.resolver_key.startsWith('threshold_state.') && !bandIdentity) ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['compute_config'], message: 'Questo resolver richiede la misura cross_ref della banda effettiva',
    });
  });
export const updateCommissioningVariableSchema = z
  .object({
    category_id: z.string().uuid().nullable().optional(),
    name: z.string().min(1).max(160).optional(),
    description: z.string().max(2000).nullable().optional(),
    resolver_key: resolverKeySchema.optional(),
    data_type: z.enum(COMMISSIONING_VARIABLE_DATA_TYPES).optional(),
    compute_config: commissioningVariableComputeConfigSchema.nullable().optional(),
    valid_from: isoDateSchema.nullable().optional(),
    valid_to: isoDateSchema.nullable().optional(),
    is_active: z.boolean().optional(),
  })
  // Task #3453 — metadata kernel aggiornabili in bozza.
  .merge(commissioningVariableKernelFieldsSchema);

// ---------------------------------------------------------------------------
// Liste di riferimento
// ---------------------------------------------------------------------------

export const createCommissioningReferenceListSchema = z.object({
  code: machineCodeSchema,
  name: z.string().min(1).max(160),
  description: z.string().max(2000).nullable().optional(),
  is_active: z.boolean().optional(),
  values: z
    .array(
      z.object({
        value: z.string().min(1).max(160),
        label: z.string().max(200).nullable().optional(),
        sort_order: z.number().int().min(0).optional(),
      })
    )
    .max(1000)
    .optional(),
});
export const updateCommissioningReferenceListSchema = z.object({
  name: z.string().min(1).max(160).optional(),
  description: z.string().max(2000).nullable().optional(),
  is_active: z.boolean().optional(),
});

/**
 * Le Funzioni sono ESCLUSIVAMENTE template di condizioni riutilizzabili:
 * nessun effetto economico incorporato. Le leve economiche si definiscono
 * SOLO nell'abbinamento configuratore×funzione (match_levers / else_levers).
 */
export const createCommissioningFunctionSchema = z.object({
  name: z.string().min(1).max(160),
  // Descrizione OBBLIGATORIA (NOT NULL a DB).
  description: z.string().min(1).max(4000),
  // Task #3453 — AST v2 (superset retro-compatibile dell'albero legacy).
  condition_tree: commissioningKernelNodeSchema,
  is_active: z.boolean().optional(),
});

export const updateCommissioningFunctionSchema = z.object({
  name: z.string().min(1).max(160).optional(),
  description: z.string().min(1).max(4000).optional(),
  condition_tree: commissioningKernelNodeSchema.optional(),
  is_active: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Cluster + membri + TGT override
// ---------------------------------------------------------------------------

export const createCommissioningClusterSchema = z.object({
  name: z.string().min(1).max(160),
  description: z.string().max(2000).nullable().optional(),
  entity_type: z.enum(COMMISSIONING_CLUSTER_ENTITY_TYPES),
  is_active: z.boolean().optional(),
});
export const updateCommissioningClusterSchema = z.object({
  name: z.string().min(1).max(160).optional(),
  description: z.string().max(2000).nullable().optional(),
  is_active: z.boolean().optional(),
});

export const createCommissioningClusterMemberSchema = z
  .object({
    company_id: z.string().uuid().nullable().optional(),
    store_id: z.string().uuid().nullable().optional(),
    user_id: z.string().uuid().nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    is_active: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    const provided = [v.company_id, v.store_id, v.user_id].filter(
      (x): x is string => typeof x === 'string'
    );
    if (provided.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Esattamente una entità tra company_id, store_id, user_id',
      });
    }
  });

export const updateCommissioningClusterMemberSchema = z.object({
  notes: z.string().max(2000).nullable().optional(),
  is_active: z.boolean().optional(),
});

/** Body degli endpoint duplica-e-modifica (functions, template, value packages). */
export const duplicateCommissioningEntitySchema = z.object({
  name: z.string().min(1).max(200).optional(),
});
export const createCommissioningCompetitionSchema = z
  .object({
    family: z.enum(COMMISSIONING_COMPETITION_FAMILIES),
    name: z.string().min(1).max(200),
    description: z.string().max(4000).nullable().optional(),
    retail_brand_id: z.string().uuid().nullable().optional(),
    channel_id: z.string().uuid().nullable().optional(),
    period_start: isoDateSchema,
    period_end: isoDateSchema,
    status: z.enum(COMMISSIONING_COMPETITION_STATUSES).optional(),
    letter_r2_key: z.string().max(1024).nullable().optional(),
    economic_context: commissioningCompetitionContextInputSchema,
    /** VP della gara abbinati alla creazione (teste vive, famiglia evento compatibile). */
    value_package_ids: z.array(z.string().uuid()).max(100).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.value_package_ids && new Set(v.value_package_ids).size !== v.value_package_ids.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['value_package_ids'], message: 'Pacchetto valenze duplicato' });
    }
    if (!commissioningParticipantTypeAllowed(v.family, v.economic_context.participant_type)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['economic_context', 'participant_type'],
        message: 'Tipo partecipante incompatibile con la famiglia della gara' });
    }
    if (v.status !== undefined && v.status !== 'draft') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['status'],
        message: 'Il contesto comune si configura in bozza e si attiva solo tramite pubblicazione' });
    }
    // Task #3484 — scoping per famiglia: insegna e sell-in richiedono
    // insegna+canale; risorse non deve salvarli affatto.
    if ((v.family === 'insegna' || v.family === 'sellin')
      && (!v.retail_brand_id || !v.channel_id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['retail_brand_id'],
        message: `Gara ${v.family === 'insegna' ? 'insegna' : 'sell-in'}: retail_brand_id e channel_id sono obbligatori`,
      });
    }
    if (v.family === 'risorse' && (v.retail_brand_id || v.channel_id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['retail_brand_id'],
        message: 'Gara risorse: retail_brand_id e channel_id non sono ammessi',
      });
    }
    if (v.channel_id && !v.retail_brand_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['channel_id'],
        message: 'channel_id richiede retail_brand_id',
      });
    }
    if (v.period_start > v.period_end) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['period_end'],
        message: 'period_end deve essere >= period_start',
      });
    }
  });
export const updateCommissioningCompetitionSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(4000).nullable().optional(),
  retail_brand_id: z.string().uuid().nullable().optional(),
  channel_id: z.string().uuid().nullable().optional(),
  period_start: isoDateSchema.optional(),
  period_end: isoDateSchema.optional(),
  status: z.enum(COMMISSIONING_COMPETITION_STATUSES).optional(),
  letter_r2_key: z.string().max(1024).nullable().optional(),
});

export const createCommissioningAcknowledgmentSchema = z.object({
  action: z.enum(COMMISSIONING_ACK_ACTIONS),
});

const gateAxisSchema = z.enum(['rs', 'pdv', 'user'] as const);
const gateCardinalitySchema = z.enum(['single', 'multi'] as const);

const gatePerimeterSchema = z.object({
  axis: gateAxisSchema,
  cardinality: gateCardinalitySchema,
});

const gateFunctionL3Schema = z.object({
  kind: z.literal('function_l3'),
  functionId: z.string().uuid(),
  layerOverride: z.enum(COMMISSIONING_INSTANCE_SCOPES).optional(),
});

const gateValenzaSchema = z.object({
  kind: z.literal('valenza'),
  threshold: z.number().finite(),
  perimeter: gatePerimeterSchema,
});

const gateFatturatoSchema = z.object({
  kind: z.literal('fatturato'),
  threshold: z.number().finite(),
  perimeter: gatePerimeterSchema,
});

const gateCountSchema = z.object({
  kind: z.literal('count'),
  threshold: z.number().int().min(1),
  perimeter: gatePerimeterSchema,
});

const gateEntityCountSchema = z.object({
  kind: z.literal('entity_count'),
  metric: z.enum(['valenza_sum', 'line_count', 'amount_sum'] as const),
  entityThreshold: z.number().finite(),
  minEntities: z.number().int().min(1),
  entityAxis: gateAxisSchema,
});

/** Schema gate v2: solo varianti tipizzate con `kind` obbligatorio. */
const templateGateSchema: z.ZodType<CommissioningGateV2> = z.discriminatedUnion('kind', [
  gateFunctionL3Schema,
  gateValenzaSchema,
  gateFatturatoSchema,
  gateCountSchema,
  gateEntityCountSchema,
]);

const multiposFavorRulesSchema = z.object({
  fromPdvCount: z.number().int().min(1),
  targetReductionPercent: z.number().min(0).max(100).optional(),
  fallbackPercent: z.number().min(0).max(100).optional(),
});

export const countingConfigSchema = z.object({
  filterFunctionId: z.string().uuid().optional(),
  // Deprecato (binari unificati): accettato in lettura, mai consumato dal runtime.
  metricVariableCode: z.string().max(80).optional(),
});

/**
 * Binario REMUNERA unificato: filtro perimetro + fonti economiche che PAGANO
 * (sottoinsieme delle fonti dichiarate in thresholdsDeclaration; la coerenza
 * col recinto dichiarato è verificata nel superRefine del template config e
 * lato service per le istanze).
 */
export const remunerationConfigSchema = z
  .object({
    filterFunctionId: z.string().uuid().optional(),
    metricVariableCode: z.string().max(80).optional(),
    paymentSources: z.array(z.enum(COMMISSIONING_THRESHOLD_SOURCES)).max(4).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.paymentSources && new Set(v.paymentSources).size !== v.paymentSources.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['paymentSources'],
        message: 'Fonti di pagamento duplicate',
      });
    }
  });

/**
 * Pattern BONUS/MALUS sul totale post-CAP (riusabile tra tipi configuratore):
 * righe funzione → ±% | ±€. Il segno lo dà la lista (bonus/malus): il valore
 * è sempre positivo.
 */
const totalBonusMalusRuleSchema = z.object({
  code: z.string().min(1).max(60),
  label: z.string().max(200).optional(),
  functionId: z.string().uuid(),
  effect: z.object({
    kind: z.enum(['percent', 'amount']),
    value: z.number().positive().finite(),
  }),
});

export const commissioningTotalBonusMalusConfigSchema = z
  .object({
    bonus: z.array(totalBonusMalusRuleSchema).max(50),
    malus: z.array(totalBonusMalusRuleSchema).max(50),
  })
  .superRefine((v, ctx) => {
    const codes = [...v.bonus, ...v.malus].map((r) => r.code);
    if (new Set(codes).size !== codes.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Codici bonus/malus duplicati (devono essere unici nel config)',
      });
    }
  });

// Task #3410 — sezioni per-tipo del config template (tutte opzionali,
// backward-compatible: i config già salvati restano validi).
const gettoneRuleSchema = z.object({
  label: z.string().max(200).optional(),
  functionId: z.string().uuid().optional(),
  amount: z.number(),
});

const bonusMalusRuleSchema = z.object({
  label: z.string().max(200).optional(),
  kpiVariableCode: z.string().min(1).max(80),
  deadBandLow: z.number().optional(),
  deadBandHigh: z.number().optional(),
  malusValue: z.number().optional(),
  bonusValue: z.number().optional(),
  functionId: z.string().uuid().optional(),
});

const rankingConfigSchema = z.object({
  metricVariableCode: z.string().max(80).optional(),
  direction: z.enum(['desc', 'asc']).optional(),
  minParticipants: z.number().int().min(1).optional(),
});

const rankingPrizeSchema = z.object({
  fromPosition: z.number().int().min(1),
  toPosition: z.number().int().min(1).optional(),
  prizeValue: z.number(),
});

const cohortKpiConfigSchema = z.object({
  metricVariableCode: z.string().max(80).optional(),
  window: z.enum(['period', 'rolling_30d', 'rolling_90d']).optional(),
  deadBandLow: z.number().optional(),
  deadBandHigh: z.number().optional(),
  bonusValue: z.number().optional(),
  malusValue: z.number().optional(),
});

const aggregationSourceSchema = z.object({
  templateId: z.string().uuid(),
  weight: z.number().min(0).optional(),
});

const templateCapDefaultsSchema = z
  .object({
    capType: z.enum(COMMISSIONING_CAP_TYPES).optional(),
    capValue: z.number().min(0).optional(),
    capScopeFilterFunctionId: z.string().uuid().optional(),
    // Task CAP evoluto — metrica del tetto (assente = amount_eur storico).
    capMetric: z.enum(COMMISSIONING_CAP_METRICS).optional(),
    // Task CAP evoluto — variabile L1 base della percentuale (storico del
    // configuratore). Obbligatoria per capType percentage_of_total.
    capBaseVariableCode: machineCodeSchema.optional(),
  })
  .superRefine((v, ctx) => {
    const hasType = v.capType !== undefined;
    const hasValue = v.capValue !== undefined;
    if (hasType !== hasValue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['capValue'],
        message: 'capType e capValue vanno valorizzati insieme',
      });
    }
    if (v.capScopeFilterFunctionId && !hasType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['capScopeFilterFunctionId'],
        message: 'Il filtro CAP richiede capType + capValue',
      });
    }
    if (v.capMetric !== undefined && !hasType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['capMetric'],
        message: 'La metrica del tetto richiede capType + capValue',
      });
    }
    if (v.capType === 'percentage_of_total' && v.capBaseVariableCode === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['capBaseVariableCode'],
        message: 'Il CAP percentuale richiede la variabile base (storico del configuratore)',
      });
    }
    if (v.capBaseVariableCode !== undefined && v.capType !== 'percentage_of_total') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['capBaseVariableCode'],
        message: 'La variabile base è ammessa solo con capType percentage_of_total',
      });
    }
    // Recinto: la percentuale insiste sull'importo €; le metriche
    // valenza/conteggio sono tetti assoluti in unità della metrica.
    if (
      v.capType === 'percentage_of_total' &&
      v.capMetric !== undefined && v.capMetric !== 'amount_eur'
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['capMetric'],
        message: 'Il CAP percentuale è ammesso solo sulla metrica importo (€)',
      });
    }
  });

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Banda Soglie nel TEMPLATE: solo struttura (identità, nome, categorie,
 * maggiorazioni, ammissione). Strict: qualsiasi valore numerico (Da/A,
 * tolleranza, fonti) è rifiutato dal salvataggio del template.
 */
export const thresholdBandStructureSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  categories: z.array(commissioningThresholdCategoryStructureSchema).max(100).optional(),
  surcharges: z.array(commissioningThresholdSurchargeStructureSchema).max(100).optional(),
  admission: z.object({ functionId: z.string().uuid() }).strict().optional(),
}).strict().superRefine((band, ctx) => {
  for (const key of ['categories', 'surcharges'] as const) {
    const ids = new Set<string>();
    (band[key] ?? []).forEach((rule, index) => {
      if (ids.has(rule.id)) ctx.addIssue({
        code: z.ZodIssueCode.custom, path: [key, index, 'id'], message: 'Identità duplicata',
      });
      ids.add(rule.id);
    });
  }
  const priorities = new Set<number>();
  (band.categories ?? []).forEach((category, index) => {
    if (priorities.has(category.priority)) ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['categories', index, 'priority'],
      message: 'Ogni categoria deve avere una priorità univoca',
    });
    priorities.add(category.priority);
  });
});

/**
 * Banda EFFETTIVA (struttura + valori composti): è la forma consumata dal
 * motore e dal simulatore. Non viene mai salvata come tale.
 */
export const thresholdEffectiveBandSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  from: z.number().finite(),
  to: z.number().finite().optional(),
  canoneMultiplier: z.number().min(0).optional(),
  gettoneGaraMultiplier: z.number().min(0).optional(),
  perUnitEur: z.number().optional(),
  flatEur: z.number().min(0).optional(),
  tolerancePct: z.number().min(0).max(99).optional(),
  categories: z.array(commissioningThresholdCategorySchema).max(100).optional(),
  surcharges: z.array(commissioningThresholdSurchargeSchema).max(100).optional(),
  admission: z.object({ functionId: z.string().uuid() }).strict().optional(),
}).strict();

/** Dichiarazione a priori del tipo soglie (mode + count + fonti economiche). */
export const thresholdsDeclarationSchema = z
  .object({
    mode: z.enum(['progressive', 'regressive']),
    countKind: z.enum(COMMISSIONING_THRESHOLD_COUNT_KINDS),
    countVariableCode: machineCodeSchema.optional(),
    countMode: commissioningThresholdCountModeSchema.optional(),
    paymentUnit: commissioningThresholdPaymentUnitSchema.optional(),
    sources: z.array(z.enum(COMMISSIONING_THRESHOLD_SOURCES)).max(4),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.countKind === 'variabile' && !v.countVariableCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['countVariableCode'],
        message: 'Tipo di count "variabile": indica la variabile L1',
      });
    }
    if (v.countKind !== 'variabile' && v.countVariableCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['countVariableCode'],
        message: 'countVariableCode è ammesso solo con countKind "variabile"',
      });
    }
    if (v.countMode === 'period_measure' && v.countKind !== 'variabile') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['countMode'],
        message: 'La misura diretta di periodo richiede una variabile di periodo' });
    }
    if (v.paymentUnit && v.countMode !== 'period_measure') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['paymentUnit'],
        message: 'Nei conteggi additivi l’unità economica è già quella dell’indicatore' });
    }
    if (new Set(v.sources).size !== v.sources.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sources'],
        message: 'Fonti economiche duplicate',
      });
    }
  });

/**
 * Invarianti della STRUTTURA Soglie (template): un solo validatore condiviso
 * da template, sezione effettiva e wizard. `path` è relativo al contenitore
 * (`thresholds`, `thresholdsDeclaration`, `remunerationDefaults`).
 */
export function refineCommissioningThresholdStructure(
  structure: {
    declaration: CommissioningThresholdDeclaration | undefined;
    bands: readonly CommissioningThresholdBandStructure[] | undefined;
    paymentSources: readonly CommissioningThresholdSource[] | undefined;
  },
  report: (path: (string | number)[], message: string) => void,
): void {
  const { declaration: decl, paymentSources: paySources } = structure;
  const bands = structure.bands ?? [];
  if (paySources !== undefined && !decl) {
    report(['remunerationDefaults', 'paymentSources'], 'Fonti di pagamento: richiede la dichiarazione soglie (thresholdsDeclaration)');
  }
  if (!decl) {
    if (bands.length > 0) report(['thresholdsDeclaration'], 'Le bande richiedono la dichiarazione dell’indicatore');
    return;
  }
  const declared = new Set(decl.sources);
  if (paySources === undefined) {
    report(['remunerationDefaults', 'paymentSources'], 'Indica esplicitamente le fonti paganti (anche nessuna)');
  } else {
    for (const s of paySources) {
      if (!declared.has(s)) report(['remunerationDefaults', 'paymentSources'], `Fonte di pagamento "${s}" non dichiarata nella dichiarazione soglie`);
    }
  }
  if (decl.countMode === 'period_measure' && decl.sources.includes('per_unit_eur')
    && (paySources === undefined || paySources.includes('per_unit_eur')) && !decl.paymentUnit) {
    report(['thresholdsDeclaration', 'paymentUnit'], 'Con un indicatore di periodo indica la base economica per gli euro per unità');
  }
  if (bands.length === 0) {
    report(['thresholds'], 'Dichiarazione soglie presente: definisci almeno una banda');
    return;
  }
  const declaredFields = new Set(decl.sources.map((s) => COMMISSIONING_THRESHOLD_SOURCE_FIELD[s]));
  const bandIds = new Set<string>();
  const names = new Set<string>();
  bands.forEach((b, i) => {
    const id = b.id.toLowerCase();
    if (!UUID_RE.test(b.id)) report(['thresholds', i, 'id'], 'La banda richiede un’identità stabile');
    if (bandIds.has(id)) report(['thresholds', i, 'id'], 'Identità banda duplicata');
    bandIds.add(id);
    const name = b.name.trim().toLowerCase();
    if (names.has(name)) report(['thresholds', i, 'name'], `Banda ${i + 1}: nome già usato da un’altra banda`);
    names.add(name);
    for (const key of ['categories', 'surcharges'] as const) {
      (b[key] ?? []).forEach((rule, index) => {
        const keys = 'parameters' in rule ? rule.parameters : rule.delta;
        for (const field of keys) {
          if (!declaredFields.has(field)) report(['thresholds', i, key, index, 'parameters' in rule ? 'parameters' : 'delta'], 'La fonte economica non è dichiarata');
        }
      });
    }
  });
}

export const templateConfigSchema = z.object({
  layer: z.enum(COMMISSIONING_INSTANCE_SCOPES).optional(),
  thresholds: z.array(thresholdBandStructureSchema).max(100).optional(),
  thresholdsDeclaration: thresholdsDeclarationSchema.optional(),
  gates: z.array(templateGateSchema).max(50).optional(),
  gettoneRules: z.array(gettoneRuleSchema).max(100).optional(),
  bonusMalusRules: z.array(bonusMalusRuleSchema).max(100).optional(),
  rankingConfig: rankingConfigSchema.optional(),
  rankingPrizes: z.array(rankingPrizeSchema).max(200).optional(),
  cohortKpi: cohortKpiConfigSchema.optional(),
  aggregationSources: z.array(aggregationSourceSchema).max(100).optional(),
  aggregationLevels: z
    .array(z.object({ minScore: z.number(), level: z.string().max(80), payout: z.number() }))
    .max(100)
    .optional(),
  capDefaults: templateCapDefaultsSchema.optional(),
  multiposFavorRules: multiposFavorRulesSchema.optional(),
  countingDefaults: countingConfigSchema.optional(),
  remunerationDefaults: remunerationConfigSchema.optional(),
  totalBonusMalus: commissioningTotalBonusMalusConfigSchema.optional(),
  extra: z.record(scalarValueSchema).optional(),
}).superRefine((cfg, ctx) => {
  refineCommissioningThresholdStructure({
    declaration: cfg.thresholdsDeclaration, bands: cfg.thresholds,
    paymentSources: cfg.remunerationDefaults?.paymentSources,
  }, (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path, message }));
});

export const createCommissioningConfiguratorTemplateSchema = z.object({
  type_id: z.string().uuid(),
  name: z.string().min(1).max(200),
  // Descrizione OBBLIGATORIA (NOT NULL a DB).
  description: z.string().min(1).max(4000),
  config: templateConfigSchema.optional(),
  is_active: z.boolean().optional(),
});
export const updateCommissioningConfiguratorTemplateSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().min(1).max(4000).optional(),
  config: templateConfigSchema.optional(),
  is_active: z.boolean().optional(),
});

export const createCommissioningConfiguratorInstanceSchema = z
  .object({
    competition_id: z.string().uuid(),
    template_id: z.string().uuid(),
    name: z.string().min(1).max(200),
    scope: z.enum(COMMISSIONING_INSTANCE_SCOPES).optional(),
    // Task #3465 — famiglia evento dichiarata dell'istanza (gate pre-L2).
    event_family: z.enum(COMMISSIONING_VP_EVENT_FAMILIES).nullable().optional(),
    period_start: isoDateSchema.nullable().optional(),
    period_end: isoDateSchema.nullable().optional(),
    multipos_favor_rules: multiposFavorRulesSchema.nullable().optional(),
    cap_scope_filter_function_id: z.string().uuid().nullable().optional(),
    cap_type: z.enum(COMMISSIONING_CAP_TYPES).nullable().optional(),
    cap_value: z.number().min(0).nullable().optional(),
    // Task CAP evoluto — metrica del tetto (null = amount_eur storico).
    cap_metric: z.enum(COMMISSIONING_CAP_METRICS).nullable().optional(),
    // Task CAP evoluto — variabile L1 base della % (storico del configuratore).
    cap_base_variable_code: machineCodeSchema.nullable().optional(),
    counting_config: countingConfigSchema.optional(),
    remuneration_config: remunerationConfigSchema.optional(),
    aggregate_rules: commissioningAggregateRulesSchema.nullable().optional(),
    is_active: z.boolean().optional(),
    /**
     * Driver di gara dell'istanza: OBBLIGATORIO in input; null esplicito =
     * istanza extra-driver (livello gara).
     */
    business_driver_id: z.string().uuid().nullable(),
    /** Driver sorgente (solo extra-driver, default []). */
    source_driver_ids: z.array(z.string().uuid()).max(200).default([]),
    /**
     * VP del configuratore, scelto fra i VP della gara (testa viva della catena
     * versioni); ereditato da tutti i cluster e le celle driver. null =
     * nessun VP (ammesso solo se il configuratore non usa fonti VP).
     */
    value_package_id: z.string().uuid().nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.business_driver_id !== null && v.source_driver_ids.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['source_driver_ids'],
        message: 'I driver sorgente sono ammessi solo su istanze extra-driver (business_driver_id null)',
      });
    }
    if (new Set(v.source_driver_ids).size !== v.source_driver_ids.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['source_driver_ids'],
        message: 'Driver sorgente duplicato',
      });
    }
    const hasCapType = v.cap_type !== null && v.cap_type !== undefined;
    const hasCapValue = v.cap_value !== null && v.cap_value !== undefined;
    if (hasCapType !== hasCapValue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cap_value'],
        message: 'cap_type e cap_value vanno valorizzati insieme',
      });
    }
    if (v.cap_scope_filter_function_id && !hasCapType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cap_scope_filter_function_id'],
        message: 'Il filtro CAP richiede cap_type + cap_value',
      });
    }
    const hasCapMetric = v.cap_metric !== null && v.cap_metric !== undefined;
    const hasCapBase = v.cap_base_variable_code !== null && v.cap_base_variable_code !== undefined;
    if (hasCapMetric && !hasCapType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cap_metric'],
        message: 'La metrica del tetto richiede cap_type + cap_value',
      });
    }
    if (v.cap_type === 'percentage_of_total' && !hasCapBase) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cap_base_variable_code'],
        message: 'Il CAP percentuale richiede la variabile base (storico del configuratore)',
      });
    }
    if (hasCapBase && v.cap_type !== 'percentage_of_total') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cap_base_variable_code'],
        message: 'La variabile base è ammessa solo con cap_type percentage_of_total',
      });
    }
    if (v.cap_type === 'percentage_of_total' && hasCapMetric && v.cap_metric !== 'amount_eur') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cap_metric'],
        message: 'Il CAP percentuale è ammesso solo sulla metrica importo (€)',
      });
    }
    if (v.period_start && v.period_end && v.period_start > v.period_end) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['period_end'],
        message: 'period_end deve essere >= period_start',
      });
    }
  });
export const updateCommissioningConfiguratorInstanceSchema =
  createCommissioningConfiguratorInstanceSchema
    .innerType()
    .omit({ competition_id: true, template_id: true })
    .partial()
    .extend({
      /** Opaque editor revision, required by the service when changing a declarative section. */
      threshold_revision: z.string().min(1).optional(),
    })
    .superRefine((v, ctx) => {
      // Coerenza driver/driver sorgente valutabile SOLO sui campi inviati; la
      // combinazione efficace (stored+patch) è validata dal service.
      if (
        v.business_driver_id !== undefined && v.business_driver_id !== null &&
        v.source_driver_ids !== undefined && v.source_driver_ids.length > 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['source_driver_ids'],
          message: 'I driver sorgente sono ammessi solo su istanze extra-driver (business_driver_id null)',
        });
      }
      if (v.source_driver_ids && new Set(v.source_driver_ids).size !== v.source_driver_ids.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['source_driver_ids'],
          message: 'Driver sorgente duplicato',
        });
      }
    });

const edgeConditionL3Schema = z.object({
  kind: z.literal('l3_function'),
  functionId: z.string().uuid(),
});

const edgeConditionParentOutcomeSchema = z.object({
  kind: z.literal('parent_outcome'),
  metric: z.enum(COMMISSIONING_EDGE_CONDITION_METRICS),
  operator: z.enum(COMMISSIONING_EDGE_CONDITION_OPERATORS),
  threshold: z.number().finite(),
});

const edgeConditionSchema = z.discriminatedUnion('kind', [
  edgeConditionL3Schema,
  edgeConditionParentOutcomeSchema,
]);

const interactionConfigSchema = z.object({
  transferPercent: z.number().min(0).max(100).optional(),
  note: z.string().max(1000).optional(),
  // Task #3494
  condition: edgeConditionSchema.optional(),
  /** Fattore moltiplicativo (arco moltiplicatore), > 0. */
  factor: z.number().gt(0).finite().optional(),
  /** Campo target del moltiplicatore (default amount_eur). */
  target: z.enum(COMMISSIONING_MOLTIPLICATORE_TARGETS).optional(),
  /** Verso di applicazione (default true = applica se condizione vera). */
  applyWhenTrue: z.boolean().optional(),
});

export const createCommissioningInteractionSchema = z
  .object({
    source_instance_id: z.string().uuid(),
    target_instance_id: z.string().uuid(),
    edge_type: z.enum(COMMISSIONING_INTERACTION_EDGE_TYPES),
    config: interactionConfigSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.source_instance_id === v.target_instance_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['target_instance_id'],
        message: 'Un arco non può collegare una istanza a se stessa',
      });
    }
  });

/**
 * Validazione DAG standalone (POST /interactions/validate): archi proposti da
 * verificare contro il grafo persistito, senza scrivere nulla.
 */
export const validateCommissioningInteractionsSchema = z.object({
  edges: z.array(createCommissioningInteractionSchema).min(1).max(200),
});

/** Identità del package (creazione: solo testata, in bozza). */
export const createCommissioningValuePackageSchema = z
  .object({
    name: z.string().min(1).max(200),
    description: z.string().max(4000).nullable().optional(),
    event_family: z.enum(COMMISSIONING_VP_EVENT_FAMILIES),
    valid_from: isoDateSchema.nullable().optional(),
    valid_to: isoDateSchema.nullable().optional(),
    is_active: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.valid_from && v.valid_to && v.valid_from > v.valid_to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['valid_to'],
        message: 'valid_to deve essere >= valid_from',
      });
    }
  });

export const updateCommissioningValuePackageSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(4000).nullable().optional(),
  event_family: z.enum(COMMISSIONING_VP_EVENT_FAMILIES).optional(),
  valid_from: isoDateSchema.nullable().optional(),
  valid_to: isoDateSchema.nullable().optional(),
  is_active: z.boolean().optional(),
});

/**
 * Nuova versione con decorrenza (POST /value-packages/:id/versions): congela
 * la versione corrente a `effective_from - 1` e attiva la copia da
 * `effective_from`. Il nome resta quello della catena salvo override.
 */
export const createCommissioningValuePackageVersionSchema = z.object({
  effective_from: isoDateSchema,
  name: z.string().min(1).max(200).optional(),
});

// ---------------------------------------------------------------------------
// Task #3450 — Catalogo add-on commerciali deterministici (v2)
// ---------------------------------------------------------------------------

/** Gestione granulare valori lista (i codici usati sono immutabili a DB). */
export const createCommissioningReferenceListValueSchema = z.object({
  value: z.string().min(1).max(160),
  label: z.string().max(200).nullable().optional(),
  sort_order: z.number().int().min(0).max(100000).optional(),
});
export const updateCommissioningReferenceListValueSchema = z.object({
  label: z.string().max(200).nullable().optional(),
  sort_order: z.number().int().min(0).max(100000).optional(),
  is_active: z.boolean().optional(),
});

/** Parametro commerciale del driver: creazione. */
export const createCommissioningAddonParameterSchema = z
  .object({
    business_driver_id: z.string().uuid(),
    code: machineCodeSchema,
    question: z.string().min(3).max(300),
    description: z.string().max(4000).nullable().optional(),
    info_category: z.string().max(120).nullable().optional(),
    value_type: z.enum(COMMISSIONING_ADDON_PARAM_TYPES),
    list_id: z.string().uuid().nullable().optional(),
    /** Sottoinsieme di valori ammessi (id di commissioning_reference_list_values). */
    allowed_value_ids: z.array(z.string().uuid()).max(500).optional(),
  })
  .superRefine((v, ctx) => {
    const isSelect = v.value_type === 'single_select' || v.value_type === 'multi_select';
    if (isSelect && !v.list_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['list_id'],
        message: 'Select e multiselect richiedono una lista esistente (mai opzioni inline)',
      });
    }
    if (isSelect && (!v.allowed_value_ids || v.allowed_value_ids.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['allowed_value_ids'],
        message: 'Dichiarare il sottoinsieme di valori ammessi della lista',
      });
    }
    if (!isSelect && (v.list_id || (v.allowed_value_ids && v.allowed_value_ids.length > 0))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['list_id'],
        message: 'Lista e valori ammessi valgono solo per select/multiselect',
      });
    }
  });

/**
 * Aggiornamento parametro: il significato (driver, code, tipo, lista, domanda)
 * è immutabile dopo l'adozione — enforcement a DB + service; qui restano i
 * campi sempre editabili e, pre-adozione, la revisione completa.
 */
export const updateCommissioningAddonParameterSchema = z.object({
  question: z.string().min(3).max(300).optional(),
  description: z.string().max(4000).nullable().optional(),
  info_category: z.string().max(120).nullable().optional(),
  /** Post-adozione il service consente SOLO aggiunte al sottoinsieme. */
  allowed_value_ids: z.array(z.string().uuid()).max(500).optional(),
});

/** Creazione add-on: identità + prima bozza (atomiche). */
export const createCommissioningAddonSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(4000).nullable().optional(),
  business_driver_id: z.string().uuid(),
  valid_from: isoDateSchema.optional(),
});
export const updateCommissioningAddonSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(4000).nullable().optional(),
});

/** Riga applicabilità della bozza (matrioska; tutte null = intero driver). */
export const commissioningAddonScopeInputSchema = z
  .object({
    category_id: z.string().uuid().nullable().optional(),
    product_type_id: z.string().uuid().nullable().optional(),
    product_id: z.string().uuid().nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if ((v.product_type_id || v.product_id) && !v.category_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['category_id'],
        message: 'Tipologia e prodotto richiedono la categoria (catena driver → categoria → tipologia → prodotto)',
      });
    }
  });

/** Utilizzo parametro nella bozza (regole di compilazione per-versione). */
export const commissioningAddonVersionParameterInputSchema = z
  .object({
    parameter_id: z.string().uuid(),
    is_required: z.boolean().optional(),
    sort_order: z.number().int().min(0).max(100000).optional(),
    help_text: z.string().max(500).nullable().optional(),
    min_selections: z.number().int().min(0).nullable().optional(),
    max_selections: z.number().int().min(1).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (
      v.min_selections !== null &&
      v.min_selections !== undefined &&
      v.max_selections !== null &&
      v.max_selections !== undefined &&
      v.min_selections > v.max_selections
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['max_selections'],
        message: 'max_selections deve essere >= min_selections',
      });
    }
  });

/** Aggiornamento integrale della bozza (replace-set atomico). */
export const updateCommissioningAddonDraftSchema = z
  .object({
    valid_from: isoDateSchema.optional(),
    notes: z.string().max(4000).nullable().optional(),
    scopes: z.array(commissioningAddonScopeInputSchema).max(200).optional(),
    parameters: z.array(commissioningAddonVersionParameterInputSchema).max(100).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.parameters) {
      const ids = new Set(v.parameters.map((p) => p.parameter_id));
      if (ids.size !== v.parameters.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['parameters'],
          message: 'Ogni parametro può comparire una sola volta nella versione',
        });
      }
    }
  });

/** Nuova bozza da una versione esistente (copia composizione e scope). */
export const createCommissioningAddonVersionSchema = z.object({
  valid_from: isoDateSchema,
});

/** Valore canonico della risposta, discriminato per tipo parametro. */
export const commissioningAddonAnswerValueSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('boolean'), value: z.boolean() }),
  z.object({ type: z.literal('number'), value: z.number().finite() }),
  z.object({ type: z.literal('text'), value: z.string().min(1).max(2000) }),
  z.object({ type: z.literal('single_select'), value_id: z.string().uuid() }),
  z.object({
    type: z.literal('multi_select'),
    value_ids: z.array(z.string().uuid()).min(1).max(200),
  }),
]);

/**
 * Risposta a UNA domanda del questionario sulla riga vendita: `no`, `mancante`
 * e `non applicabile` sono stati distinti; `value` presente SOLO se answered.
 */
export const commissioningSaleAddonAnswerSchema = z
  .object({
    parameter_id: z.string().uuid(),
    state: z.enum(COMMISSIONING_ADDON_ANSWER_STATES),
    value: commissioningAddonAnswerValueSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.state === 'answered' && !v.value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value'],
        message: 'Risposta answered senza valore',
      });
    }
    if (v.state !== 'answered' && v.value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value'],
        message: 'Valore ammesso solo con stato answered',
      });
    }
  });

/**
 * Risposte add-on per una riga vendita (payload cassa). La validazione
 * puntuale contro il questionario risolto (required, tipo, valori ammessi,
 * min/max multiselect) è server-side dentro la transazione di vendita.
 */
export const commissioningSaleAddonResponsesInputSchema = z.object({
  answers: z.array(commissioningSaleAddonAnswerSchema).max(100),
});

// ---------------------------------------------------------------------------
// Tipi derivati (z.infer — MAI duplicati a mano)
// ---------------------------------------------------------------------------

export type CreateCommissioningVariableCategoryInput = z.infer<
  typeof createCommissioningVariableCategorySchema
>;
export type UpdateCommissioningVariableCategoryInput = z.infer<
  typeof updateCommissioningVariableCategorySchema
>;
export type CreateCommissioningVariableInput = z.infer<typeof createCommissioningVariableSchema>;
export type UpdateCommissioningVariableInput = z.infer<typeof updateCommissioningVariableSchema>;
export type CreateCommissioningReferenceListInput = z.infer<
  typeof createCommissioningReferenceListSchema
>;
export type UpdateCommissioningReferenceListInput = z.infer<
  typeof updateCommissioningReferenceListSchema
>;
export type CreateCommissioningFunctionInput = z.infer<typeof createCommissioningFunctionSchema>;
export type UpdateCommissioningFunctionInput = z.infer<typeof updateCommissioningFunctionSchema>;
export type CreateCommissioningClusterInput = z.infer<typeof createCommissioningClusterSchema>;
export type UpdateCommissioningClusterInput = z.infer<typeof updateCommissioningClusterSchema>;
export type CreateCommissioningClusterMemberInput = z.infer<
  typeof createCommissioningClusterMemberSchema
>;

export type UpdateCommissioningClusterMemberInput = z.infer<
  typeof updateCommissioningClusterMemberSchema
>;
export type CreateCommissioningCompetitionInput = z.infer<
  typeof createCommissioningCompetitionSchema
>;
export type UpdateCommissioningCompetitionInput = z.infer<
  typeof updateCommissioningCompetitionSchema
>;
export type CreateCommissioningAcknowledgmentInput = z.infer<
  typeof createCommissioningAcknowledgmentSchema
>;
export type CreateCommissioningConfiguratorTemplateInput = z.infer<
  typeof createCommissioningConfiguratorTemplateSchema
>;
export type UpdateCommissioningConfiguratorTemplateInput = z.infer<
  typeof updateCommissioningConfiguratorTemplateSchema
>;
export type CreateCommissioningConfiguratorInstanceInput = z.infer<
  typeof createCommissioningConfiguratorInstanceSchema
>;
export type UpdateCommissioningConfiguratorInstanceInput = z.infer<
  typeof updateCommissioningConfiguratorInstanceSchema
>;
export type CreateCommissioningInteractionInput = z.infer<
  typeof createCommissioningInteractionSchema
>;

/**
 * Task #3494 FASE 2 — Aggiornamento di un arco DAG esistente: solo config
 * (edge_type incluso per il cambio da/verso moltiplicatore). Il source/target
 * non cambiano mai (sarebbe una cancellazione + ri-creazione).
 */
export const updateCommissioningInteractionSchema = z.object({
  edge_type: z.enum(COMMISSIONING_INTERACTION_EDGE_TYPES).optional(),
  config: interactionConfigSchema.optional(),
});
export type CreateCommissioningValuePackageInput = z.infer<
  typeof createCommissioningValuePackageSchema
>;
export type UpdateCommissioningValuePackageInput = z.infer<
  typeof updateCommissioningValuePackageSchema
>;

export type CommissioningVpProductBaseInput = z.infer<
  typeof commissioningVpProductBaseInputSchema
>;

export type CreateCommissioningReferenceListValueInput = z.infer<
  typeof createCommissioningReferenceListValueSchema
>;
export type UpdateCommissioningReferenceListValueInput = z.infer<
  typeof updateCommissioningReferenceListValueSchema
>;
export type CreateCommissioningAddonParameterInput = z.infer<
  typeof createCommissioningAddonParameterSchema
>;
export type UpdateCommissioningAddonParameterInput = z.infer<
  typeof updateCommissioningAddonParameterSchema
>;
export type CreateCommissioningAddonInput = z.infer<typeof createCommissioningAddonSchema>;
export type UpdateCommissioningAddonInput = z.infer<typeof updateCommissioningAddonSchema>;
export type CommissioningAddonScopeInput = z.infer<typeof commissioningAddonScopeInputSchema>;
export type CommissioningAddonVersionParameterInput = z.infer<
  typeof commissioningAddonVersionParameterInputSchema
>;
export type UpdateCommissioningAddonDraftInput = z.infer<
  typeof updateCommissioningAddonDraftSchema
>;
export type CreateCommissioningAddonVersionInput = z.infer<
  typeof createCommissioningAddonVersionSchema
>;
export type CommissioningAddonAnswerValueInput = z.infer<
  typeof commissioningAddonAnswerValueSchema
>;
export type CommissioningSaleAddonAnswerInput = z.infer<typeof commissioningSaleAddonAnswerSchema>;
export type CommissioningSaleAddonResponsesInput = z.infer<
  typeof commissioningSaleAddonResponsesInputSchema
>;

export type DuplicateCommissioningEntityInput = z.infer<typeof duplicateCommissioningEntitySchema>;

export type ValidateCommissioningInteractionsInput = z.infer<
  typeof validateCommissioningInteractionsSchema
>;

/** Creazione periodo di competenza (bounds validati vs gara nel service). */
export const createCommissioningPeriodSchema = z
  .object({
    label: z.string().trim().min(1).max(120),
    period_start: isoDateSchema,
    period_end: isoDateSchema,
  })
  .refine((v) => v.period_start <= v.period_end, {
    message: 'period_start deve precedere (o coincidere con) period_end',
    path: ['period_end'],
  });

export type CommissioningPeriodLockActionInput = z.infer<
  typeof commissioningPeriodLockActionSchema
>;

/** Lock/unlock periodo (azione auditata: note facoltative). */
export const commissioningPeriodLockActionSchema = z.object({
  notes: z.string().trim().min(1).max(500).optional(),
});

/**
 * Avanzamento attesa amministrativa gara ricevuta:
 * documented richiede documento del brand + importo documentato.
 */
export const updateCommissioningReceivableExpectationSchema = z
  .object({
    status: z.enum(['documented', 'reconciled']),
    document_ref: z.string().trim().min(1).max(255).optional(),
    documented_amount: z.number().finite().optional(),
    notes: z.string().trim().min(1).max(1000).optional(),
  })
  .refine(
    (v) => v.status !== 'documented'
      || (v.document_ref !== undefined && v.documented_amount !== undefined),
    {
      message: 'Lo stato documented richiede document_ref e documented_amount',
      path: ['status'],
    },
  );

export type UpdateCommissioningReceivableExpectationInput = z.infer<
  typeof updateCommissioningReceivableExpectationSchema
>;

export type UpdateCommissioningPayoutStatusInput = z.infer<
  typeof updateCommissioningPayoutStatusSchema
>;

export type CreateCommissioningPayoutAdjustmentInput = z.infer<
  typeof createCommissioningPayoutAdjustmentSchema
>;

export type CreateCommissioningPeriodInput = z.infer<typeof createCommissioningPeriodSchema>;

/** Aggiustamento payout: riga etichettata, delta non nullo. */
export const createCommissioningPayoutAdjustmentSchema = z.object({
  amount: z.number().finite().refine((n) => n !== 0, 'Il delta non può essere zero'),
  label: z.string().trim().min(1).max(255),
});

/** Avanzamento stato payout (maturato→approvato→liquidato, monotono). */
export const updateCommissioningPayoutStatusSchema = z.object({
  status: z.enum(['approvato', 'liquidato']),
  notes: z.string().trim().min(1).max(1000).optional(),
});

export type ReplaceCommissioningVpProductsInput = z.infer<
  typeof replaceCommissioningVpProductsSchema
>;

/** Anteprima runtime (stesso contratto del motore). */
export const previewCommissioningValuePackageSchema = z.object({
  product_id: z.string().uuid(),
  /** Canone mensile della vendita simulata (monthly_fee). */
  monthly_fee: z.number().min(0).nullable().optional(),
  /** Fatti simulati per le funzioni kernel collegate al VP (code → valore). */
  facts: z
    .record(
      z.union([
        z.boolean(),
        z.number().finite(),
        z.string().max(300),
        z.array(z.string().max(160)).max(200),
      ]),
    )
    .optional(),
});

/** Base prodotto (fotografia piatta product_id → valori). */
export const commissioningVpProductBaseInputSchema = z.object({
  product_id: z.string().uuid(),
  valenza: z.number().finite(),
  gettone_contrattuale: z.number().finite(),
  gettone_gara: z.number().finite(),
  canone_multiplier: z.number().finite().nullable().optional(),
  /** Monthly_fee fotografato dal listino canvas (immutabile dopo creazione). */
  canone_base_offerta: z.number().finite().nonnegative().nullable().optional(),
  /** Importo contrattuale di gara, editabile in bozza. */
  canone_gara: z.number().finite().nonnegative().nullable().optional(),
});

/** Sostituzione integrale delle basi prodotto (PUT /:id/products). */
export const replaceCommissioningVpProductsSchema = z
  .object({
    products: z.array(commissioningVpProductBaseInputSchema).max(2000),
  })
  .superRefine((v, ctx) => {
    const ids = new Set(v.products.map((p) => p.product_id));
    if (ids.size !== v.products.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['products'],
        message: 'Prodotti duplicati nella fotografia basi',
      });
    }
  });

export type PreviewCommissioningValuePackageInput = z.infer<
  typeof previewCommissioningValuePackageSchema
>;

export type UpdateCommissioningInteractionInput = z.infer<
  typeof updateCommissioningInteractionSchema
>;

// ---------------------------------------------------------------------------
// Task #3529 — Picker listino-first: schemi input
// ---------------------------------------------------------------------------

/**
 * Valori economici opzionali per prodotto (usati nel batch add con valori).
 * Tutti i campi sono opzionali — se omessi si usano i default (zero / null).
 */
export const vpProductValuesSchema = z.object({
  valenza: z.number().finite().optional(),
  gettone_contrattuale: z.number().finite().optional(),
  gettone_gara: z.number().finite().optional(),
  canone_multiplier: z.number().finite().nullable().optional(),
  canone_gara: z.number().finite().nonnegative().nullable().optional(),
});

export type VpProductValuesInput = z.infer<typeof vpProductValuesSchema>;

/**
 * Riga del picker listino-first: un prodotto + listino sorgente + valori economici opzionali.
 * Usato nel nuovo contratto del batch add (Task #3529).
 */
export const vpProductRowInputSchema = z.object({
  product_id:    z.string().uuid(),
  price_list_id: z.string().uuid(),
  /** Fee mensile letta dal listino; facoltativa, il backend la ri-legge dal DB. */
  monthly_fee: z.number().finite().nonnegative().optional(),
}).merge(vpProductValuesSchema);

export type VpProductRowInput = z.infer<typeof vpProductRowInputSchema>;

/**
 * Aggiunta batch di prodotti al VP — due contratti supportati:
 *
 * **Nuovo (picker listino-first, Task #3529)**:
 *   `{ products: [{ product_id, price_list_id, monthly_fee?, valori? }] }`
 *   → rifiuta duplicati con VP_PRODUCT_DUPLICATE (400).
 *
 * **Legacy (sezione Copertura, idempotente)**:
 *   `{ product_ids: string[], price_list_overrides?, bulk_values?, per_product_values? }`
 *   → skip silenzioso se il prodotto è già presente.
 */
export const vpAddProductsWithValuesSchema = z.union([
  /** Nuovo contratto picker listino-first: lista strutturata prodotto × listino × valori. */
  z.object({
    products: z.array(vpProductRowInputSchema).min(1).max(200),
  }),
  /** Contratto legacy: lista piatta di product_id con overrides opzionali. */
  z.object({
    product_ids: z.array(z.string().uuid()).min(1).max(200),
    /**
     * Quale listino usare per ogni prodotto (product_id → price_list_id).
     * Se omesso per un prodotto, si usa il primo listino sorgente del VP.
     */
    price_list_overrides: z.record(z.string().uuid(), z.string().uuid()).optional(),
    /** Valori applicati a tutti i prodotti del batch. */
    bulk_values: vpProductValuesSchema.optional(),
    /**
     * Override per-prodotto (chiave = product_id, valore = valori economici).
     * Vince su bulk_values per il prodotto specifico.
     */
    per_product_values: z.record(z.string().uuid(), vpProductValuesSchema).optional(),
  }),
]);

export type VpAddProductsWithValuesInput = z.infer<typeof vpAddProductsWithValuesSchema>;


// ---------------------------------------------------------------------------
// Task CAP evoluto — Simulatore di configurazione (wizard Riepilogo)
// ---------------------------------------------------------------------------

/**
 * Simulazione della configurazione del wizard su un evento tipo descritto
 * dall'utente. Lavora sul `config` del draft (anche NON salvato) e riusa le
 * stesse funzioni del motore (nessun secondo valutatore): conta → leve →
 * banda → remunera → CAP → bonus/malus, con importi a ogni stadio.
 * Oggi supportato per il tipo `soglie` (modello dichiarativo a 6 stadi).
 */
export const simulateCommissioningTemplateConfigSchema = z.object({
  /** Tipo del configuratore (recinto: solo il modello dichiarativo soglie). */
  type_code: z.literal('soglie'),
  /** Config del template come lo produrrebbe il salvataggio del wizard (solo struttura). */
  config: templateConfigSchema,
  /**
   * Valori di prova digitati nel simulatore del wizard (MAI salvati nel
   * template): compongono la struttura come farebbero i valori di un cluster.
   */
  test_values: commissioningThresholdValuesSchema,
  /** Evento tipo descritto dall'utente. */
  event: commissioningTemplateSimulationEventSchema,
  scenario: commissioningThresholdPeriodScenarioSchema.optional(),
  /** Storico simulato dell'istanza PRIMA dell'evento (default: tutto a 0). */
  history: z
    .object({
      /** Unità di count cumulate prima dell'evento (banda progressiva/crossing). */
      units_before: z.number().finite().min(0).optional(),
      /** Righe vive prima dell'evento (crossing bande con from=0). */
      lines_before: z.number().int().min(0).optional(),
      /** Maturato € del periodo prima dell'evento (base CAP importo). */
      prior_amount_sum: z.number().finite().min(0).optional(),
      /** Valenza cumulata prima dell'evento (base CAP valenza). */
      prior_valenza_sum: z.number().finite().min(0).optional(),
      /** Numero righe del periodo prima dell'evento (base CAP conteggio). */
      prior_line_count: z.number().int().min(0).optional(),
      /** Storico della variabile-base CAP % prima dell'evento. */
      prior_base_sum: z.number().finite().min(0).optional(),
    })
    .optional(),
  /**
   * Istanza esistente del template per lo stadio LEVE: valuta gli abbinamenti
   * istanza×funzione reali (delta valenza/gettoni/fattore canone). Assente =
   * stadio leve informativo (le leve vivono sugli abbinamenti dell'istanza).
   */
  instance_id: z.string().uuid().optional(),
}).superRefine((input, ctx) => {
  const scenario = input.scenario;
  if (!scenario) return;
  const issue = (path: (string | number)[], message: string) =>
    ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
  if (input.instance_id) issue(['instance_id'], 'Lo scenario sintetico non mescola abbinamenti live di un’istanza');
  if (Object.values(input.history ?? {}).some((value) => value !== undefined && value !== 0)) {
    issue(['history'], 'I cumulati non sostituiscono gli eventi del periodo e non possono essere sommati di nuovo');
  }
  if (scenario.period_start > scenario.period_end) issue(['scenario', 'period_end'], 'Fine periodo precedente all’inizio');
  const ids = new Set<string>();
  const check = (event: z.infer<typeof commissioningTemplateSimulationEventSchema>, path: (string | number)[]) => {
    if (!event.id) issue([...path, 'id'], 'Identità stabile dell’evento necessaria');
    else if (ids.has(event.id.toLowerCase())) issue([...path, 'id'], 'Evento duplicato nello scenario');
    else ids.add(event.id.toLowerCase());
    if (!event.occurred_at) issue([...path, 'occurred_at'], 'Data e ordine dell’evento necessari');
    else {
      const date = new Date(event.occurred_at);
      if (!Number.isFinite(date.getTime())) return;
      const day = date.toISOString().slice(0, 10);
      if (day < scenario.period_start || day > scenario.period_end) {
        issue([...path, 'occurred_at'], 'Evento fuori dal periodo simulato (UTC)');
      }
    }
  };
  scenario.events_before.forEach((event, index) => check(event, ['scenario', 'events_before', index]));
  check(input.event, ['event']);
});

export type SimulateCommissioningTemplateConfigInput = z.infer<
  typeof simulateCommissioningTemplateConfigSchema
>;

